import { memo, useCallback, useState } from 'react';

import { useDrag } from 'react-dnd';
import { useDispatch, useSelector } from 'react-redux';

import { MODAL_DELETE } from '@/components/organisms/Modals/ModalConductor';
import AuthPageGuidelinesPanel from '@/components/pages/Authentication/panels/AuthPageGuidelinesPanel';
import { AuthStepKey } from '@/components/pages/Authentication/types';
import useAuthPageValidation from '@/components/pages/Authentication/useAuthPageValidation';
import { cn } from '@/lib/utils';
import { selectHasError } from '@/redux/dialogAlerts/selectors';
import { TYPES } from '@/redux/dialogs/helper';
import { popModal, pushModal } from '@/redux/modals/actions';
import { removeAction, selectAction, selectAuthPanel } from '@/redux/nodes/actions';
import {
  selectIsActionSelected,
  selectIsIdInFlow,
  selectIsNodeConditionRequisiteSelected,
  selectSelectedAuthPanel,
} from '@/redux/nodes/selectors';
import { SubAgentAction } from '@/shared/types/action';
import { AuthConfig, AuthPanel } from '@/shared/types/node';
import { isKeyEnter, preventClickThrough } from '@/util/util';

import AuthStepsCanvas from './AuthStepsCanvas';
import { useAuthSubAgentConfig } from './useAuthSubAgentConfig';
import ActionHeader from '../../ActionHeader/ActionHeader';
import { BoxErrorIcon } from '../Action/Action';
import ActionPlaceholder from '../Placeholder/Placeholder';

interface Props {
  action: SubAgentAction;
  actionId: string;
  nodeId: string;
  requisiteIndex?: number;
  conditionIndex?: number;
}

const AuthActionCard = memo(
  ({ action, actionId, nodeId, requisiteIndex = null, conditionIndex = null }: Props) => {
    const dispatch = useDispatch();
    const isSelected = useSelector(selectIsActionSelected(actionId));
    const isActionInFlow = useSelector(selectIsIdInFlow(actionId));
    const isAnythingSelected = useSelector(selectIsNodeConditionRequisiteSelected);
    const hasDialogError = useSelector(selectHasError(actionId));
    const selectedAuthPanel = useSelector(selectSelectedAuthPanel);

    const { authConfig, onUpdate } = useAuthSubAgentConfig(actionId, action);
    const { getFieldError } = useAuthPageValidation(authConfig, isSelected);
    const identError = getFieldError('identification-questions');
    const verifyError = getFieldError('verification-questions');
    const hasSuccessNodeError = !!getFieldError('success_node_id');
    const hasFailureNodeError = !!getFieldError('failure_node_id');

    const [guidelinesStep, setGuidelinesStep] = useState<AuthStepKey | null>(null);

    const [, dragRef] = useDrag({
      item: { actionId, nodeId, requisiteIndex, conditionIndex, move: true, type: action.type },
      type: TYPES.ACTION,
      canDrag: () => !document?.activeElement?.className?.match(/DraftEditor|input/),
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    });

    const handleSelect = useCallback(
      (panel: AuthPanel) => {
        if (!isSelected) {
          dispatch(selectAction({ actionId, authPanel: panel }));
        } else {
          dispatch(selectAuthPanel({ panel }));
        }
      },
      [actionId, dispatch, isSelected]
    );

    const handleBoxClick = useCallback(
      (e: React.MouseEvent) => {
        preventClickThrough(e);
        // Default to identification step when clicking outside the inline subparts.
        handleSelect(selectedAuthPanel ?? 'step-identification');
      },
      [handleSelect, selectedAuthPanel]
    );

    const handleBoxKeyUp = useCallback(
      (e: React.KeyboardEvent) => {
        if (isKeyEnter(e)) {
          handleSelect(selectedAuthPanel ?? 'step-identification');
        }
      },
      [handleSelect, selectedAuthPanel]
    );

    const handleDelete = useCallback(() => {
      dispatch(
        pushModal(MODAL_DELETE, {
          title: 'Remove authentication',
          subtitle: 'Are you sure you want to remove this authentication action?',
          onDelete: () => {
            dispatch(removeAction({ actionId }));
            dispatch(popModal());
          },
        })
      );
    }, [actionId, dispatch]);

    const handleToggleStep = useCallback(
      (step: AuthStepKey, enabled: boolean) => {
        const stepKey = `${step}_step` as const;
        const currentStep = authConfig[stepKey];
        onUpdate({ [stepKey]: { ...currentStep, enabled } } as Partial<AuthConfig>);
        handleSelect(`step-${step}`);
      },
      [authConfig, handleSelect, onUpdate]
    );

    return (
      <>
        <ActionPlaceholder
          nodeId={nodeId}
          beforeActionId={actionId}
          requisiteIndex={requisiteIndex}
          conditionIndex={conditionIndex}
        />

        <section
          id={actionId}
          className={cn(
            'relative mx-auto mt-[13px] w-80',
            // Blue arrow connecting this card to whatever sits above it, centered
            // over the (single-column) card.
            'before:absolute before:bottom-full before:left-1/2 before:z-[1] before:h-[18px] before:w-2 before:-translate-x-1/2',
            "before:bg-[image:url('/connecting-line.svg')] before:bg-no-repeat before:content-['']"
          )}
        >
          {hasDialogError && <BoxErrorIcon />}
          {/* Plain div (not Box): Box has overflow:hidden which would clip, and
              a fixed 240px width. */}
          <div
            ref={(node) => {
              dragRef(node);
            }}
            className={cn(
              'relative flex w-80 flex-col items-stretch rounded border bg-background transition',
              isSelected ? 'border-informational' : 'border-stroke',
              isSelected && hasDialogError && 'border-destructive ring-2 ring-destructive',
              !isActionInFlow && !isAnythingSelected && 'opacity-50'
            )}
            role="button"
            tabIndex={0}
            onClick={handleBoxClick}
            onKeyUp={handleBoxKeyUp}
            data-testid="auth-action-card"
          >
            <ActionHeader
              type="sub_agent"
              objectToCopy={action}
              isSelected={isSelected}
              onDelete={handleDelete}
              hasDialogError={hasDialogError}
            />
            {/* Inline pieces handle their own selection; don't bubble to the card. */}
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
            <div className="p-3" onClick={(e) => e.stopPropagation()}>
              <AuthStepsCanvas
                authConfig={authConfig}
                selectedPanel={isSelected ? (selectedAuthPanel ?? null) : null}
                identificationError={identError}
                verificationError={verifyError}
                successBoxHasError={hasSuccessNodeError}
                failureBoxHasError={hasFailureNodeError}
                onSelectPanel={handleSelect}
                onToggleStep={handleToggleStep}
                onOpenGuidelines={setGuidelinesStep}
              />
            </div>
          </div>
        </section>

        {guidelinesStep && (
          <AuthPageGuidelinesPanel
            key={guidelinesStep}
            step={guidelinesStep}
            authConfig={authConfig}
            onUpdate={onUpdate}
            onClose={() => setGuidelinesStep(null)}
          />
        )}
      </>
    );
  }
);

AuthActionCard.displayName = 'AuthActionCard';

export default AuthActionCard;
